import React from "react";
import { SCROLL_OFFSET, ARTICLE_COLUMN, H2, BODY } from "./sections";

const COLUMNS = {
  windows: "Windows Voice Access",
  ai: "AI Voice Typing (e.g. Zero Typing)",
};

const ROWS = [
  {
    feature: "Works across apps",
    windows: "Yes, but accuracy varies a lot by app",
    ai: "Yes, consistent experience everywhere",
  },
  {
    feature: "Punctuation handling",
    windows: 'Often requires spoken commands ("period," "comma")',
    ai: "Inferred automatically from speech",
  },
  {
    feature: "Custom vocabulary",
    windows: "Limited, manual correction required",
    ai: "Personal Dictionary learns names, jargon, spellings",
  },
  {
    feature: "Tone or style control",
    windows: "None",
    ai: "Prompt-based rewriting for tone, grammar, format",
  },
  {
    feature: "Setup",
    windows: "Built in, no install",
    ai: "Lightweight download",
  },
  {
    feature: "Offline use",
    windows: "Yes",
    ai: "Varies by tool",
  },
];

// Two-tone blue surface with white gridlines, as in the design: a slightly
// deeper band for the header row, the pale callout blue for every body cell.
const HEADER_BG = "bg-[#D9EDFC]";
const CELL_BG = "bg-[#EBF7FE]";

// White hairlines separate both rows and columns.
const GRID_LINE = "border-white";

const HEAD_CELL = `align-top ${HEADER_BG} px-[16px] py-[17.17px] text-left font-inter text-[13px] font-semibold leading-[20px] tracking-[0px] text-[#0A0D16] lg:px-[21.46px]`;

const BODY_CELL = `align-top ${CELL_BG} px-[16px] py-[16px] font-inter text-[13px] leading-[20px] tracking-[0px] lg:px-[21.46px]`;

const WindowsComparisonSection = () => {
  return (
    <section
      id="windows-dictation-vs-ai-voice-typing"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className={ARTICLE_COLUMN}
    >
      <h2 className={H2}>Windows Dictation vs AI Voice Typing</h2>

      <div className="pt-[25.76px]">
        {/*
          Below md the three columns cannot fit without shrinking text past
          legibility, so each row becomes a self-contained card. The table
          renders from md up.
        */}
        <div className="flex flex-col gap-[12px] md:hidden">
          {ROWS.map(({ feature, windows, ai }) => (
            <article
              key={feature}
              className={`overflow-hidden rounded-[12px] ${CELL_BG}`}
            >
              <h3
                className={`${HEADER_BG} px-[16px] py-[12px] font-inter text-[14px] font-semibold leading-[22px] text-[#0A0D16]`}
              >
                {feature}
              </h3>

              <div className={`border-b-[1.5px] ${GRID_LINE} px-[16px] py-[14px]`}>
                <p className="font-inter text-[13px] font-semibold leading-[20px] text-[#0A0D16]">
                  {COLUMNS.windows}
                </p>
                <p className="mt-[4px] font-inter text-[13px] font-normal leading-[20px] text-[#0A0D16B2]">
                  {windows}
                </p>
              </div>

              <div className="px-[16px] py-[14px]">
                <p className="font-inter text-[13px] font-semibold leading-[20px] text-[#0A0D16]">
                  {COLUMNS.ai}
                </p>
                <p className="mt-[4px] font-inter text-[13px] font-normal leading-[20px] text-[#0A0D16B2]">
                  {ai}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Table - md and up */}
        <div className="hidden overflow-hidden rounded-[12px] md:block">
          <table className="w-full table-fixed border-collapse">
            <colgroup>
              <col className="w-[30%]" />
              <col className="w-[35%]" />
              <col className="w-[35%]" />
            </colgroup>

            <thead>
              <tr>
                {/* Empty corner cell, as in the design */}
                <th
                  scope="col"
                  className={`${HEAD_CELL} border-b-[1.5px] border-r-[1.5px] ${GRID_LINE}`}
                >
                  <span className="sr-only">Feature</span>
                </th>
                <th
                  scope="col"
                  className={`${HEAD_CELL} border-b-[1.5px] border-r-[1.5px] ${GRID_LINE}`}
                >
                  {COLUMNS.windows}
                </th>
                <th
                  scope="col"
                  className={`${HEAD_CELL} border-b-[1.5px] ${GRID_LINE}`}
                >
                  {COLUMNS.ai}
                </th>
              </tr>
            </thead>

            <tbody>
              {ROWS.map(({ feature, windows, ai }, index) => {
                const rowLine =
                  index === ROWS.length - 1
                    ? ""
                    : `border-b-[1.5px] ${GRID_LINE}`;

                return (
                  <tr key={feature}>
                    <th
                      scope="row"
                      className={`${BODY_CELL} ${rowLine} border-r-[1.5px] ${GRID_LINE} text-left font-medium text-[#0A0D16]`}
                    >
                      {feature}
                    </th>
                    <td
                      className={`${BODY_CELL} ${rowLine} border-r-[1.5px] ${GRID_LINE} font-normal text-[#0A0D16B2]`}
                    >
                      {windows}
                    </td>
                    <td
                      className={`${BODY_CELL} ${rowLine} font-normal text-[#0A0D16B2]`}
                    >
                      {ai}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className={`mt-[30.05px] ${BODY}`}>
        Windows Voice Access is genuinely useful for accessibility and basic
        hands-free navigation. Where it struggles is long-form dictation for
        actual writing. Spoken punctuation commands break your flow, and
        there&apos;s no way to tell it &quot;make this sound more formal&quot;
        or &quot;clean up the filler words.&quot; You get a rough transcript,
        then you&apos;re back to editing it manually anyway.
      </p>
    </section>
  );
};

export default WindowsComparisonSection;
